import { useCallback, useEffect, useState } from "react";
import { FileSearch } from "lucide-react";
import { theme, card, emptyFrame, btnPrimary, inputStyle, type as text } from "../theme";
import { StatusTag, type StatusTone } from "./Drawing";
import { ControlPicker } from "./ControlPicker";
import { api, apiErrorMessage } from "../api/client";
import { fmtDate } from "../lib/format";
import { secondaryBtn } from "../pages/resilience/shared";

// Assurance reports (docs/TPRM-SUPPLIER-ASSURANCE.md "Assurance reports", backend lib/assuranceReportReading.ts).
// A supplier's SOC 2 report or ISO certificate pack, already in the Evidence Repository, read for its type, scope,
// period, opinion, exceptions and complementary user entity controls, each with the words it was read from. Nothing
// counts until a member confirms it; a confirmed complementary control is mapped to one of the organisation's own
// controls. Only confirmed items reach the supplier's signals. Every write is tprm:write on the server.

type Kind = "report_type" | "scope" | "period" | "opinion" | "exception" | "cuec";
interface Item {
  id: string; kind: Kind; label: string; text: string; value: unknown; quote: string; source: "rule" | "ai";
  status: "pending" | "confirmed" | "edited" | "rejected"; decidedText: string | null; controlId: string | null; controlLabel: string | null;
}
interface Report {
  id: string; fileName: string; documentVersion: number; status: string; error: string | null; aiUsed: boolean; aiNote: string | null;
  truncated: boolean; dropped: number; createdAt: string; items: Item[];
}
interface View { aiAvailable: boolean; reports: Report[] }
interface Candidate { id: string; fileName: string; version: number; uploadedAt: string }

const STATUS_TONE: Record<Item["status"], StatusTone> = { pending: "partial", confirmed: "met", edited: "met", rejected: "none" };
const STATUS_LABEL: Record<Item["status"], string> = { pending: "To confirm", confirmed: "Confirmed", edited: "Confirmed (edited)", rejected: "Not accepted" };
const ORDER: Kind[] = ["report_type", "period", "opinion", "scope", "exception", "cuec"];
const REPORT_TYPES: Array<[string, string]> = [["soc2_type1", "SOC 2 Type 1"], ["soc2_type2", "SOC 2 Type 2"], ["soc1_type1", "SOC 1 Type 1"], ["soc1_type2", "SOC 1 Type 2"], ["iso_certificate", "ISO certificate"], ["other", "Other assurance report"]];
const OPINIONS: Array<[string, string]> = [["unqualified", "Unqualified (clean)"], ["qualified", "Qualified"], ["adverse", "Adverse"], ["disclaimer", "Disclaimer of opinion"]];

export function SupplierAssuranceReports({ supplierId, canWrite, onChanged }: { supplierId: string; canWrite: boolean; onChanged: () => void }) {
  const [view, setView] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const base = `/tprm/suppliers/${supplierId}`;

  const load = useCallback(async () => {
    try { setView(await api.get<View>(`${base}/assurance-reports`)); setError(null); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the assurance reports.")); }
  }, [base]);
  useEffect(() => { void load(); }, [load]);

  // A reading in progress is polled until it finishes.
  const running = view?.reports.some((r) => r.status === "queued" || r.status === "running");
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => { void load(); }, 3000);
    return () => clearInterval(t);
  }, [running, load]);
  useEffect(() => { if (view && !running) onChanged(); }, [running]); // eslint-disable-line react-hooks/exhaustive-deps

  async function openChooser() {
    setChoosing(true); setError(null);
    try { setCandidates((await api.get<{ documents: Candidate[] }>("/tprm/signed-copy-candidates")).documents); }
    catch (e) { setError(apiErrorMessage(e, "Could not list the Evidence Repository")); }
  }
  async function read() {
    if (!picked) return;
    setBusy(true); setError(null);
    try { setView(await api.post<View>(`${base}/assurance-reports`, { documentId: picked })); setChoosing(false); setPicked(""); }
    catch (e) { setError(apiErrorMessage(e, "Could not start reading the report")); }
    finally { setBusy(false); }
  }
  const decided = (v: View) => { setView(v); onChanged(); };

  if (!view) return error ? <div role="alert" style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div> : null;
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
        <div style={{ ...text.sectionTitle, margin: 0, display: "flex", alignItems: "center", gap: 6 }}><FileSearch size={15} /> Assurance reports</div>
        {canWrite && !choosing && <button type="button" style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12 }} onClick={() => void openChooser()}>Read a report</button>}
      </div>
      <div style={{ ...text.hint, marginBottom: 8 }}>
        The supplier's SOC 2 report or ISO certificate pack, read for its scope, period, opinion, exceptions and the controls it says you must operate, each with the words it was read from. Nothing counts until you confirm it.
        {!view.aiAvailable && " Below the Self-Service + AI tier only the report type and period are read."}
      </div>
      {error && <div role="alert" style={{ ...text.hint, color: theme.color.dangerText, marginBottom: 8 }}>{error}</div>}
      {choosing && (
        <div style={{ ...card, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          <select aria-label="Report to read" value={picked} onChange={(e) => setPicked(e.target.value)} style={{ ...inputStyle, minWidth: 280, flex: 1 }}>
            <option value="">Choose a document from the Evidence Repository…</option>
            {candidates.map((c) => <option key={c.id} value={c.id}>{c.fileName} (v{c.version}, {fmtDate(c.uploadedAt)})</option>)}
          </select>
          <button type="button" disabled={!picked || busy} style={{ ...btnPrimary, padding: "4px 12px", fontSize: 12 }} onClick={() => void read()}>Read it</button>
          <button type="button" style={secondaryBtn} onClick={() => setChoosing(false)}>Cancel</button>
        </div>
      )}
      {view.reports.length === 0 && !choosing && <div style={{ ...emptyFrame, ...text.hint }}>No assurance report read for this supplier yet. Upload it to the Evidence Repository, then read it here.</div>}
      {view.reports.map((r) => <ReportCard key={r.id} report={r} base={base} canWrite={canWrite} onDecided={decided} />)}
    </div>
  );
}

function ReportCard({ report, base, canWrite, onDecided }: { report: Report; base: string; canWrite: boolean; onDecided: (v: View) => void }) {
  const items = [...report.items].sort((a, b) => ORDER.indexOf(a.kind) - ORDER.indexOf(b.kind));
  const pending = items.filter((i) => i.status === "pending").length;
  return (
    <div style={{ ...card, marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div style={{ ...text.body, fontWeight: 600, color: theme.color.heading }}>{report.fileName} <span style={text.meta}>v{report.documentVersion}, read {fmtDate(report.createdAt)}</span></div>
        {report.status === "done" ? <StatusTag tone={pending ? "partial" : "met"}>{pending ? `${pending} to confirm` : "All decided"}</StatusTag>
          : report.status === "error" ? <StatusTag tone="gap">Not read</StatusTag> : <StatusTag tone="none">Reading…</StatusTag>}
      </div>
      {report.error && <div style={{ ...text.hint, color: theme.color.dangerText }}>{report.error}</div>}
      {report.aiNote && <div style={{ ...text.hint }}>{report.aiNote}</div>}
      {report.truncated && <div style={{ ...text.hint }}>Only the start of this long report was read.</div>}
      {report.dropped > 0 && <div style={text.meta}>{report.dropped} reading{report.dropped === 1 ? "" : "s"} left out because the words quoted were not in the report.</div>}
      {items.length > 0 && (
        <div style={{ display: "grid", gap: 0, marginTop: 8 }}>
          {items.map((i, n) => <ItemRow key={i.id} item={i} first={n === 0} base={base} canWrite={canWrite} onDecided={onDecided} />)}
        </div>
      )}
    </div>
  );
}

function ItemRow({ item, first, base, canWrite, onDecided }: { item: Item; first: boolean; base: string; canWrite: boolean; onDecided: (v: View) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.text);
  const [value, setValue] = useState<Record<string, string>>(() => {
    const v = (item.value ?? {}) as Record<string, string | null>;
    return { type: v.type ?? "other", opinion: v.opinion ?? "unqualified", start: v.start ?? "", end: v.end ?? "" };
  });
  const [controlId, setControlId] = useState<string | null>(item.controlId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(decision: "confirm" | "edit" | "reject") {
    setBusy(true); setError(null);
    const body: Record<string, unknown> = { decision };
    if (decision === "edit") {
      if (item.kind === "report_type") body.value = { type: value.type };
      else if (item.kind === "opinion") body.value = { opinion: value.opinion };
      else if (item.kind === "period") body.value = { start: value.start || null, end: value.end };
      else body.text = draft;
    }
    if (item.kind === "cuec" && decision !== "reject") body.controlId = controlId;
    try { onDecided((await api.post<{ view: View }>(`${base}/assurance-items/${item.id}`, body)).view); setEditing(false); }
    catch (e) { setError(apiErrorMessage(e, "Could not record the decision")); }
    finally { setBusy(false); }
  }
  async function map(next: string | null) {
    setControlId(next);
    setBusy(true); setError(null);
    try { onDecided(await api.put<View>(`${base}/assurance-items/${item.id}/control`, { controlId: next })); }
    catch (e) { setError(apiErrorMessage(e, "Could not map the control")); }
    finally { setBusy(false); }
  }

  const shown = item.decidedText ?? item.text;
  const confirmed = item.status === "confirmed" || item.status === "edited";
  return (
    <div style={{ padding: "8px 0", borderTop: first ? "none" : `1px solid ${theme.color.border}` }}>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={text.label}>{item.label}{item.source === "rule" ? "" : " · read by AI"}</div>
          <div style={{ ...text.body, color: item.status === "rejected" ? theme.color.faint : theme.color.text }}>{shown}</div>
          <blockquote style={{ ...text.hint, margin: "4px 0 0", paddingLeft: 8, borderLeft: `2px solid ${theme.color.border}`, fontStyle: "italic" }}>{item.quote}</blockquote>
        </div>
        <StatusTag tone={STATUS_TONE[item.status]}>{STATUS_LABEL[item.status]}</StatusTag>
      </div>
      {item.kind === "cuec" && item.status !== "rejected" && (
        <div style={{ marginTop: 6 }}>
          <div style={{ ...text.meta, marginBottom: 2 }}>Your control that answers it{confirmed && !item.controlId ? " (not mapped yet)" : ""}</div>
          {canWrite ? <ControlPicker value={controlId} onChange={(n) => { if (confirmed) void map(n.controlId); else setControlId(n.controlId); }} disabled={busy} />
            : <div style={text.hint}>{item.controlLabel ?? "Not mapped"}</div>}
        </div>
      )}
      {canWrite && item.status === "pending" && !editing && (
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <button type="button" disabled={busy} style={{ ...btnPrimary, padding: "3px 10px", fontSize: 12 }} onClick={() => void decide("confirm")}>Confirm</button>
          <button type="button" disabled={busy} style={secondaryBtn} onClick={() => setEditing(true)}>Edit</button>
          <button type="button" disabled={busy} style={secondaryBtn} onClick={() => void decide("reject")}>Not right</button>
        </div>
      )}
      {editing && (
        <div style={{ display: "flex", gap: 6, marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
          {item.kind === "report_type" && <select aria-label="Report type" value={value.type} onChange={(e) => setValue({ ...value, type: e.target.value })} style={{ ...inputStyle, width: 220 }}>{REPORT_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}
          {item.kind === "opinion" && <select aria-label="Opinion" value={value.opinion} onChange={(e) => setValue({ ...value, opinion: e.target.value })} style={{ ...inputStyle, width: 220 }}>{OPINIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>}
          {item.kind === "period" && <>
            <input type="date" aria-label="Period start" value={value.start} onChange={(e) => setValue({ ...value, start: e.target.value })} style={{ ...inputStyle, width: 160 }} />
            <span style={text.hint}>to</span>
            <input type="date" aria-label="Period end" value={value.end} onChange={(e) => setValue({ ...value, end: e.target.value })} style={{ ...inputStyle, width: 160 }} />
          </>}
          {(item.kind === "scope" || item.kind === "exception" || item.kind === "cuec") && <input aria-label="What the report says" value={draft} onChange={(e) => setDraft(e.target.value)} style={{ ...inputStyle, flex: 1, minWidth: 260 }} maxLength={600} />}
          <button type="button" disabled={busy} style={{ ...btnPrimary, padding: "3px 10px", fontSize: 12 }} onClick={() => void decide("edit")}>Save</button>
          <button type="button" style={secondaryBtn} onClick={() => setEditing(false)}>Cancel</button>
        </div>
      )}
      {error && <div role="alert" style={{ ...text.hint, color: theme.color.dangerText }}>{error}</div>}
    </div>
  );
}
